import { useCallback, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import numeral from 'numeral';
import produce from 'immer';
import { flatten, isNumber } from 'lodash';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { extractFacetExpression, getDelta, getDisplayValue } from 'common/util/graph';
import { useEventCache } from 'hooks/useLRQ/useEventCache';
import { useGlobal } from 'hooks/useGlobal';
import { useSetSearchParams } from 'hooks/useSetSearchParams';
import { QueryType } from 'common/graphqlTypes';
import { PlotModel, UrlParams } from 'common/types';
import { getRequestId } from 'common/util/lrqRequest';

import Dropdown from 'components/common/Dropdown/Dropdown';
import { queryTimeRangeState, searchParamRecordState } from 'components/common/Managers/RouteStateManager.state';
import { plotDataRequestsState } from 'components/search/SearchLogsPage/SearchStateManager.state';
import { useResizeRegistration } from 'components/common/Managers/LayoutStateManager';
import PlotGroupPicker from './PlotGroupPicker';
import SelectionDeltas from './SelectionDeltas';

import './SearchGraphLegend.scss';

type AggregateFunction = {
  text: string;
  name: string;
  clickHandler?: () => void;
  active?: boolean;
};

type SearchGraphLegendProps = {
  plotGroups: Record<string, PlotModel[]>;
  timeStamps: Array<number>;
};

const AGGREGATE_DROPDOWN: Array<Array<AggregateFunction>> = [
  [
    { text: 'Mean', name: 'mean' },
    { text: 'Max', name: 'max' },
    { text: 'Min', name: 'min' },
    { text: 'SumPerSec', name: 'sumPerSec' },
  ],
  [
    { text: '10th %ile', name: 'p10' },
    { text: '50th %ile', name: 'p50' },
    { text: '90th %ile', name: 'p90' },
    { text: '95th %ile', name: 'p95' },
    { text: '99th %ile', name: 'p99' },
    { text: '99.9 %ile', name: 'p999' },
  ],
];

const HOUR_SEC = 3600; // The number of seconds in 1 hour.
const SEC_MS = 1000; // The number of seconds in a MS.
const MAX_BREAKDOWN_PLOTS = 19;
/**
 * Graph lengend component for the search page. This allows user to hide/show plots and
 * apply different aggregate functions for current graph.
 */
function SearchGraphLegend({
  plotGroups,
  timeStamps,
}: SearchGraphLegendProps): JSX.Element {

  const searchParams = useRecoilValue(searchParamRecordState);

  const setSearchParams = useSetSearchParams();
  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const { startTime, endTime } = queryTimeRange;
  const [ selectionRange ] = useGlobal(K.GLOBAL_KEY.CHART_SELECTION_RANGE);
  const parsedFacet = extractFacetExpression(searchParams.facet || '');
  const [ aggregateFunction, setAggregateFunction ] = useState(parsedFacet.facetFunction || 'mean');
  const plotDataRequests = useRecoilValue(plotDataRequestsState);
  const facetDataId = getRequestId(plotDataRequests, QueryType.TOP_FACETS);
  const { results: [{ facetData }] } = useEventCache([ facetDataId ]);

  const selectedPlots = flatten(Object.values(plotGroups)).filter(plot => plot.visible);

  const displayPlotGroups = produce(plotGroups, (draft) => {
    Object.values(draft || {}).forEach((plots) => {
      plots.forEach((plot) => {
        let start = parseFloat(startTime);
        let end = parseFloat(endTime);
        if (selectionRange.start && selectionRange.end) {
          start = selectionRange.start;
          end = selectionRange.end;
        }
        // If breakdown numeric facet value graph, we need to keep in mind an aggregate function was applied to the facet.
        // TODO: breakdown graphs `op` property really should reflect their aggregate operation applied. Right now, they use
        // their label  as `op` so toggling of plots has backward compatability with angular.
        const appliedAggregateFunc = (!!searchParams.facet && !!searchParams.breakdownFacet) ? aggregateFunction : null;
        const displayValue = getDisplayValue(plot, appliedAggregateFunc, start, end, timeStamps);
        const formatValue = isNumber(displayValue) ? numeral(displayValue).format('0,0[.][00000] a').toUpperCase() : null;
        plot.value = formatValue;
      });
    });
  });

  const selectionDeltas = {
    delta: '0',
    deltaHour: '0',
    deltaSec: '0',
  };
  if (selectionRange.start && selectionRange.end && selectedPlots.length === 1) {
    const delta = getDelta(selectedPlots?.[0] || [], selectionRange.start, selectionRange.end, timeStamps);
    const deltaHour = isNumber(delta) ? delta * HOUR_SEC : null;
    const deltaSec =  isNumber(delta) ? (delta * SEC_MS) / (selectionRange.end - selectionRange.start) : null;
    selectionDeltas.delta = numeral(delta).format('0,0[.][00000] a').toUpperCase();
    selectionDeltas.deltaHour = numeral(deltaHour).format('0,0[.][00000] a').toUpperCase();
    selectionDeltas.deltaSec = numeral(deltaSec).format('0,0[.][00000] a').toUpperCase();
  }

  const setClickHandler = (funcName: string) => (): void => {

    const facetExp = extractFacetExpression(searchParams.facet || '');
    setSearchParams({ [K.URL_PARAM.FACET]: `${funcName}(${facetExp.facetVariable || ''})` } as UrlParams);
    setAggregateFunction(funcName);
  };

  const getAggregateFunctionsDropdown = (): JSX.Element | null => {
    const shouldRenderForBreakdown = searchParams.facet && searchParams.facet !== K.RATE.op && searchParams.breakdownFacet;
    const shouldRenderForComparison = searchParams.facet && searchParams.facet !== K.RATE.op && searchParams.compareTimeOffset;

    if (shouldRenderForBreakdown || shouldRenderForComparison) {
      const aggregateFunctionLabel = parsedFacet?.facetFunction || K.MEAN.op;
      const aggregateFunctionSections: Array<Array<AggregateFunction>> = [];
      AGGREGATE_DROPDOWN.forEach((section) => {
        const mappedSection = section.map((item) => {
          const dropdownItem = {
            ...item,
            clickHandler: setClickHandler(item.name),
            active: item.name.toLowerCase() === aggregateFunctionLabel.toLowerCase(),
          };
          return dropdownItem;
        });
        aggregateFunctionSections.push(mappedSection);
      });

      const selectedFunction = aggregateFunctionSections.flat().find((dropdownItem) => !!dropdownItem.active);
      return (
        <Dropdown
          sections={aggregateFunctionSections}
          itemAs="button"
        >
          Show:&nbsp;
          {selectedFunction?.text}
        </Dropdown>
      );
    }

    if (aggregateFunction !== K.MEAN.op) {
      // reset to default state
      setAggregateFunction(K.MEAN.op);
    }
    return null;
  };

  const handleTogglePlot = (plotToToggle: PlotModel): void  => {
    const showGraphsList = searchParams.showGraphs ? JSON.parse(searchParams.showGraphs) : [];
    const hideGraphsList = searchParams.hideGraphs ? JSON.parse(searchParams.hideGraphs) : [];
    const showGraphs = new Set(showGraphsList);
    const hideGraphs = new Set(hideGraphsList);

    if (plotToToggle.visible) {
      showGraphs.delete(plotToToggle.op);
      hideGraphs.add(plotToToggle.op);
    } else {
      showGraphs.add(plotToToggle.op);
      hideGraphs.delete(plotToToggle.op);
    }
    setSearchParams({
      [K.URL_PARAM.SHOW_GRAPHS]: JSON.stringify(Array.from(showGraphs)),
      [K.URL_PARAM.HIDE_GRAPHS]: JSON.stringify(Array.from(hideGraphs)),
    } as UrlParams, false);
  };

  const handleTogglePlotGroup = (title: string, toggleState: boolean): void  => {
    const showGraphsList = searchParams.showGraphs ? JSON.parse(searchParams.showGraphs) : [];
    const hideGraphsList = searchParams.hideGraphs ? JSON.parse(searchParams.hideGraphs) : [];
    const showGraphs = new Set(showGraphsList);
    const hideGraphs = new Set(hideGraphsList);

    const plotsToToggle = plotGroups?.[title].map((plot) => plot.op);
    plotsToToggle?.forEach((plot) => {
      if (toggleState) {
        showGraphs.add(plot);
        hideGraphs.delete(plot);
      } else {
        showGraphs.delete(plot);
        hideGraphs.add(plot);
      }
    });
    setSearchParams({
      [K.URL_PARAM.SHOW_GRAPHS]: JSON.stringify(Array.from(showGraphs)),
      [K.URL_PARAM.HIDE_GRAPHS]: JSON.stringify(Array.from(hideGraphs)),
    } as UrlParams, false);
  };

  const breakdownFacetObj = useMemo(
    () => facetData?.facets?.find((facet) => facet.name === searchParams.breakdownFacet),
    [facetData, searchParams.breakdownFacet],
  );

  const getTotalPossibleBreakdownPlots = useCallback((): number | undefined => breakdownFacetObj?.uniqueValuesCount, [breakdownFacetObj]);

  const renderPossibleValuesHint = (): JSX.Element | null => {
    if (!searchParams.breakdownFacet) {
      return null;
    }
    const breakdownPlots = Object.values(displayPlotGroups || {})?.[0] || [];
    const shouldRenderHint = breakdownPlots.length;
    const totalNumberOfPlots = getTotalPossibleBreakdownPlots();
    return shouldRenderHint && totalNumberOfPlots
      ? <>
          <p className="SearchGraphLegend__Hint">{`Showing ${breakdownPlots.length} of ${totalNumberOfPlots} possible values`}</p>
          <p className="SearchGraphLegend__Hint">{`Breakdown charts support up to ${MAX_BREAKDOWN_PLOTS} plots`}</p>
        </>
      : null;
  };

  const sideBarContainerRef = useResizeRegistration();

  return (
    <div className="SearchGraphLegend" ref={sideBarContainerRef} data-resize-id={K.RESIZE_ID.SEARCH_GRAPHS_LEGEND}>
      { getAggregateFunctionsDropdown() }
      {
        Object.entries(displayPlotGroups || {}).map(([key, value]) => (
          <PlotGroupPicker
            key={key}
            title={key}
            plots={value}
            capitalizeLabels={!searchParams.breakdownFacet}
            togglePlot={handleTogglePlot}
            togglePlotGroup={handleTogglePlotGroup}
          />
        ))
      }
      { renderPossibleValuesHint() }
      { selectedPlots.length === 1 && <SelectionDeltas
        selectionDeltas={selectionDeltas}
        testId={ELEMENT.searchGraphSelectionDeltas}
      />}
    </div>
  );
}

export default SearchGraphLegend;
